import {
  collection,
  query,
  where,
  QuerySnapshot,
  Query,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { AgentRequest } from '@usacsmideo/types';
import { db, auth } from '../constants/firebaseModular';
import { useFirestoreListener } from './useFirestoreListener';

/**
 * ECN Subtask 9 (CU-86ahjxrgg) — the relationship-directional data layer behind
 * the persona-polymorphic My MIDEO Family surface (redesign §3.1) AND the
 * `/sessions` "My Family's Scans" grouping (built later, reuses
 * `relationshipsWhereImAgent`).
 *
 * Two directions over the top-level `agentRequests` collection:
 *   - `myAgents`                  — `senderId == me` (the agents I designated).
 *   - `relationshipsWhereImAgent` — `recipientId == me && status == accepted`
 *                                   (the patients I'm an accepted agent of).
 *
 * Why two single-field listeners instead of one: a doc is never both mine-as-
 * sender and mine-as-recipient, so there is no overlap to dedup, and querying
 * each direction by its own equality field keeps us on Firestore's AUTOMATIC
 * single-field indexes — no composite index to declare/deploy (status is
 * partitioned client-side in the selectors). This deliberately avoids the
 * index-deploy-ordering risk that the S8 `(recipientEmail, status)` composite
 * carried.
 *
 * Status filtering lives in the selectors, not the query, because the patient
 * view wants to SEE multiple statuses (waiting + accepted, and archived/rejected
 * for history), while the agent view wants only `accepted`. Fetch-all-by-
 * direction + partition-in-memory serves both without extra indexes.
 */

const AGENT_REQUESTS = 'agentRequests';

function toAgentRequests(snapshot: QuerySnapshot): AgentRequest[] {
  return snapshot.docs.map((d: QueryDocumentSnapshot) => ({
    id: d.id,
    ...(d.data() as Omit<AgentRequest, 'id'>),
  }));
}

export interface MyAgentsPartition {
  /** Live invites awaiting acceptance (`status === "waiting"`). */
  pending: AgentRequest[];
  /** Accepted agents (`status === "accepted"`). */
  accepted: AgentRequest[];
  /** Terminal history (`rejected` | `archived`). */
  inactive: AgentRequest[];
  /** All of the above, raw. */
  all: AgentRequest[];
}

/**
 * Patient direction: the agents this user has designated. Returns the raw query
 * plus a `partition` of the data by lifecycle status for the patient view.
 */
export function useMyAgents({ enabled = true }: { enabled?: boolean } = {}) {
  const uid = auth.currentUser?.uid;
  const safeUid = uid || '_placeholder';

  const result = useFirestoreListener<Query, AgentRequest[]>({
    target: query(collection(db, AGENT_REQUESTS), where('senderId', '==', safeUid)),
    queryKey: ['agentRequests', 'bySender', uid],
    label: 'myAgents',
    enabled: enabled && !!uid,
    transform: toAgentRequests,
  });

  const all = result.data ?? [];
  const partition: MyAgentsPartition = {
    pending: all.filter((r) => r.status === 'waiting'),
    accepted: all.filter((r) => r.status === 'accepted'),
    inactive: all.filter((r) => r.status === 'rejected' || r.status === 'archived'),
    all,
  };

  return { ...result, partition };
}

/**
 * Agent direction: the patients this user is an ACCEPTED agent of. Powers the
 * agent-only My MIDEO Family view and (later) `/sessions` "My Family's Scans".
 * Status is filtered in-memory to `accepted` — a waiting/rejected/archived
 * request does not make you someone's agent.
 */
export function useRelationshipsWhereImAgent({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const uid = auth.currentUser?.uid;
  const safeUid = uid || '_placeholder';

  const result = useFirestoreListener<Query, AgentRequest[]>({
    target: query(collection(db, AGENT_REQUESTS), where('recipientId', '==', safeUid)),
    queryKey: ['agentRequests', 'byRecipient', uid],
    label: 'relationshipsWhereImAgent',
    enabled: enabled && !!uid,
    transform: toAgentRequests,
  });

  const accepted = (result.data ?? []).filter((r) => r.status === 'accepted');

  return { ...result, accepted };
}
